// @flow
import * as React from 'react';
import { withTheme } from 'styled-components';
import {
    get, isEmpty, sortBy,
} from 'lodash';
import {
    Box,
} from 'grommet';
import { IntlShape } from 'react-intl';
import ActionButtons from 'esm-web-pro/components/forms/ActionButtons';
import { ApiClient, connectAPI } from 'speed-js-react';
import { HTTP_400_BAD_REQUEST } from 'speed-js-core';
import Spinner from 'esm-web-pro/components/forms/Spinner';
import Point11ReorderPoint12 from './Point11ReorderPoint12';
import Point11ReorderCables from './Point11ReorderCables';


type Props = {
    point: Object,
    onClose: () => void,
    intl: IntlShape,
    setOrder: (data: Object, resource: ?string) => Promise<Object>,
    updateObject: Function,
}

type State = {
    changedPoints12: Object,
    changedDisconCables: Object,
    changedPatchCables: Object,
    points12InputErrors: Object,
    cablesInputErrors: Object,
    patchCablesInputErrors: Object,
    isSubmitting: boolean,
};


@connectAPI((apiClient: ApiClient) => ({
    setOrder: {
        send: (data: Object, resource: string = 'point11') => get(apiClient.resources, resource).reorder(data),
    },
}), true)
class Point11Reorder extends React.Component<Props, State> {
    state = {
        changedPoints12: {},
        changedDisconCables: {},
        changedPatchCables: {},
        points12InputErrors: {},
        cablesInputErrors: {},
        patchCablesInputErrors: {},
        patchCables: {},
        isSubmitting: false,
    };

    mounted = false;

    componentDidMount(): void {
        const { point: { connected_cables: { layers1_2: points12 } } } = this.props;
        this.mounted = true;

        const patchCables = {};
        Object.keys(points12).forEach((key) => {
            Object.values(points12[key].cables).forEach((cable: Object) => {
                if (cable.patch && !patchCables[cable.id]) {
                    patchCables[cable.id] = cable;
                }
            });
        });
        this.setState({ patchCables });
    }

    componentWillUnmount(): void {
        this.mounted = false;
    }

    handlePoint12InputChange = (e, currentPoint12) => {
        const { point, intl: { formatMessage } } = this.props;
        const { points12InputErrors, changedPoints12 } = this.state;
        const { connected_cables: { layers1_2: points12 } } = point;

        const inputNumber = Number(e.target.value);
        let mergedPoints12 = { ...points12, ...changedPoints12 };

        const initialPoint = Object.values(mergedPoints12).find((point12) => {
            if (point12.type === currentPoint12.type) {
                return point12.sequence === inputNumber;
            }
            return false;
        });

        const additionalData = currentPoint12.sequence !== inputNumber ? {
            [currentPoint12.id]: {
                ...currentPoint12,
                sequence: inputNumber,
            },
        } : (delete changedPoints12[currentPoint12.id]);

        // Setted new value and error
        if (initialPoint && initialPoint.id !== currentPoint12.id) {
            points12InputErrors[currentPoint12.id] = {
                sequence: inputNumber,
                type: currentPoint12.type,
            };
            this.setState({
                points12InputErrors,
                changedPoints12: {
                    ...changedPoints12,
                    ...additionalData,
                },
            });
        } else {
            if ((Object.prototype.hasOwnProperty.call(points12InputErrors, currentPoint12.id))) {
                delete points12InputErrors[currentPoint12.id];
            }

            this.setState({
                points12InputErrors,
                changedPoints12: {
                    ...changedPoints12,
                    ...additionalData,
                },
            });
        }

        mergedPoints12 = { ...mergedPoints12, [currentPoint12.id]: { ...currentPoint12, sequence: inputNumber } };

        // Updated old errors
        Object.keys(points12InputErrors).forEach((key) => {
            const errs = Object.values(mergedPoints12).find((val) => {
                if (mergedPoints12[key].id !== val.id && mergedPoints12[key].type === val.type) {
                    return val.sequence === mergedPoints12[key].sequence;
                }
                return false;
            });
            if (!errs) {
                delete points12InputErrors[key];
                this.setState({ points12InputErrors });
            }
        });
    };

    handleInputChange = (e, currentObject, errorsWithName, changedObjectsWithName, initialObjects) => {
        const errorsName = Object.keys(errorsWithName)[0];
        const changedObjectsName = Object.keys(changedObjectsWithName)[0];
        const errors = errorsWithName[errorsName];
        const changedObjects = changedObjectsWithName[changedObjectsName];

        const inputNumber = Number(e.target.value);
        let mergedObjects = { ...initialObjects, ...changedObjects };

        const initialObject = Object.values(mergedObjects).find((object) => object.sequence === inputNumber);

        const additionalData = currentObject.sequence !== inputNumber ? {
            [currentObject.id]: {
                ...currentObject,
                sequence: inputNumber,
            },
        } : (delete changedObjects[currentObject.id]);

        // Setted new value and error
        if (initialObject && initialObject.id !== currentObject.id) {
            errors[currentObject.id] = { sequence: inputNumber };
            this.setState({
                [errorsName]: errors,
                [changedObjectsName]: {
                    ...changedObjects,
                    ...additionalData,
                },
            });
        } else {
            if (Object.prototype.hasOwnProperty.call(errors, currentObject.id)) {
                delete errors[currentObject.id];
            }

            this.setState({
                [errorsName]: errors,
                [changedObjectsName]: {
                    ...changedObjects,
                    ...additionalData,
                },
            });
        }

        mergedObjects = { ...mergedObjects, [currentObject.id]: { ...currentObject, sequence: inputNumber } };

        Object.keys(errors).forEach((key) => {
            const errs = Object.values(mergedObjects).find((val) => {
                if (mergedObjects[key].id !== val.id) {
                    return val.sequence === mergedObjects[key].sequence;
                }
                return false;
            });
            if (!errs) {
                delete errors[key];
                this.setState({ [errorsName]: errors });
            }
        });
    };

    handleCableInputChange = (e, currentCable) => {
        const { cablesInputErrors, changedDisconCables } = this.state;
        const { point: { disconnected_cables: disconCables } } = this.props;

        this.handleInputChange(e, currentCable, { cablesInputErrors },
            { changedDisconCables }, disconCables);
    };

    handlePatchCableInputChange = (e, currentCable) => {
        const { patchCablesInputErrors, changedPatchCables, patchCables } = this.state;

        this.handleInputChange(e, currentCable, { patchCablesInputErrors },
            { changedPatchCables }, patchCables);
    };

    getFormContent = () => {
        const { point, intl } = this.props;
        const {
            points12InputErrors, cablesInputErrors, patchCablesInputErrors,
            changedPoints12, changedDisconCables, changedPatchCables, isSubmitting, patchCables,
        } = this.state;
        const { connected_cables: { layers1_2: points12 }, disconnected_cables: disconCables } = point;

        const points12ByType = Object.values(sortBy(points12, ['type', 'sequence']));
        const cablesBySequence = sortBy(disconCables, ['sequence']);

        return (
            <Box pad={{ left: '27px' }} direction="column" gap="small" flex overflow="auto">
                { !isSubmitting && points12ByType.map((point12, index) => (
                    <Point11ReorderPoint12
                        key={`point12-${point12.id}`}
                        points12List={points12ByType}
                        point12={point12}
                        index={index}
                        inputErrors={points12InputErrors}
                        changedPoints12={changedPoints12}
                        handleInputChange={this.handlePoint12InputChange}
                        intl={intl}
                    />
                )) }
                { !isSubmitting && cablesBySequence.map((cable, index) => (
                    <Point11ReorderCables
                        key={`cable-${cable.id}`}
                        cableList={cablesBySequence}
                        cable={cable}
                        index={index}
                        inputErrors={cablesInputErrors}
                        changedDisconCables={changedDisconCables}
                        handleInputChange={this.handleCableInputChange}
                        intl={intl}
                    />
                )) }
                { !isSubmitting && sortBy(patchCables, ['sequence']).map((cable, index) => (
                    <Point11ReorderCables
                        key={`cable-${cable.id}`}
                        cableList={patchCables}
                        cable={cable}
                        index={index}
                        inputErrors={patchCablesInputErrors}
                        changedDisconCables={changedPatchCables}
                        handleInputChange={this.handlePatchCableInputChange}
                        intl={intl}
                    />
                )) }
            </Box>
        );
    };

    handleSave = () => {
        const {
            point, setOrder, updateObject, onClose,
        } = this.props;
        const { connected_cables: { layers1_2: points12 }, disconnected_cables: disconCables } = point;
        const {
            changedPoints12, changedDisconCables, changedPatchCables,
            points12InputErrors, cablesInputErrors, patchCablesInputErrors,
        } = this.state;

        if (!isEmpty(changedPoints12) || !isEmpty(changedDisconCables) || !isEmpty(changedPatchCables)) {
            this.setState({ isSubmitting: true });
            setOrder({
                layers1_2: changedPoints12,
                cables: changedDisconCables,
                patch_cables: changedPatchCables,
                container_id: point.id,
            })
                .then((response) => {
                    if (response.status === HTTP_400_BAD_REQUEST) {
                        this.setState({
                            points12InputErrors: {
                                ...points12InputErrors,
                                ...[response.data.err_points12][0],
                            },
                            cablesInputErrors: {
                                ...cablesInputErrors,
                                ...[response.data.err_cables][0],
                            },
                            patchCablesInputErrors: {
                                ...patchCablesInputErrors,
                                ...[response.data.err_patch_cables][0],
                            },
                            isSubmitting: false,
                        });
                    } else {
                        if (this.mounted) {
                            this.setState({ isSubmitting: false });
                        }
                        const newPoint = { ...point };

                        newPoint.connected_cables.layers1_2 = { ...points12, ...[response.data.layers1_2][0] };
                        Object.keys(newPoint.connected_cables.layers1_2).forEach((pointKey) => {
                            const { cables } = newPoint.connected_cables.layers1_2[pointKey];
                            Object.keys(cables).forEach((cableKey) => {
                                if (cables[cableKey].id in response.data.patch_cables) {
                                    newPoint.connected_cables.layers1_2[pointKey]
                                        .cables[cableKey] = response.data.patch_cables[cables[cableKey].id];
                                }
                            });
                        });
                        newPoint.disconnected_cables = { ...disconCables, ...[response.data.cables][0] };
                        updateObject(newPoint);
                        onClose();
                    }
                });
        } else if (isEmpty(changedPoints12) && isEmpty(changedDisconCables)) {
            onClose();
        }
    };

    render() {
        const { onClose } = this.props;
        const {
            points12InputErrors, cablesInputErrors, patchCablesInputErrors, isSubmitting,
        } = this.state;


        return (
            <Box height="100%" align="center">
                <Box
                    direction="row"
                    gap="medium"
                    align="center"
                    height="100%"
                    width="100%"
                >
                    <Box alignSelf="stretch" flex>
                        <Spinner
                            type="clipLoader"
                            loading={isSubmitting}
                            blocking={false}
                            color="black"
                            coverStyle={{
                                position: 'relative',
                                width: 'auto',
                                height: 'auto',
                                top: '35%',
                                padding: '30px',
                            }}
                        />
                        {this.getFormContent()}
                        <ActionButtons
                            onSave={this.handleSave}
                            onClose={onClose}
                            disabled={!isEmpty(points12InputErrors) || !isEmpty(cablesInputErrors)
                            || !isEmpty(patchCablesInputErrors) || isSubmitting}
                        />
                    </Box>
                </Box>
            </Box>
        );
    }
}

export default withTheme(Point11Reorder);
